import { memo, useCallback, useEffect, useRef, useState } from "react";
import { type CurrentLocation } from "../../salesPartnerTypes";
import { Controller, useFormContext } from "react-hook-form";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import {
  trackSalesPartnerFinderDeselectCountry,
  trackSalesPartnerFinderSelectCountry,
  trackSalesPartnerFinderSelectPreferred,
} from "../../../feature/Analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import { usePreferredSalesPartner } from "~/SalesPartner/components/UsePreferredSalesPartner";
import { Button } from "~/common/components/Button/Button";
import { Alert, Box, Grid, type SelectChangeEvent } from "@mui/material";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import GroupedSelect, { type GroupedSelectRef, type Suggestion } from "../GroupedSelect/GroupedSelect";
import SPFinderFilters, { type SPFinderFiltersRef } from "../SPFinderFilters/SPFinderFilters";
import classes from "./SalesPartnerSearchBox.module.less";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import Modal from "~/common/components/Modal";
import { RadiusSelectButtonGroup } from "./GooglePlacesInput/RadiusSelectButtonGroup";
import { useGoogleAutocomplete } from "~/SalesPartner/api/getAutocomplete";
import { throttle } from "lodash";
import { getCompanyList } from "~/SalesPartner/api/getCompanyList";
import SearchIcon from "@mui/icons-material/Search";
import { trackPartnerFinderSearch } from "~/feature/Analytics/ga/salesPartner";

interface Props {
  usersCurrentLocation: CurrentLocation | null;
  markersLoading: boolean;
  pageCountryCode: string;
  listOfPartnersNames: string[];
  resetForm: () => void;
  submitForm: () => void;
  productCompetencesOptions?: Option[];
  certifiedCompetencesOptions?: Option[];
  isCheckout?: boolean;
}

export enum SearchTypesSP {
  Partner = "partner",
  Address = "address",
}

export interface SASearchFormFields {
  address: string;
  partner: string;
  radius: number;
  filterByCountry: boolean;
  filterBySalesPartner: boolean;
  searchType: SearchTypesSP;
  productCompetence?: string[];
  certifiedCompetence?: string[];
}

export interface Option {
  key: string;
  label: string;
}

const SalesPartnerFinderSearchBox = (props: Props) => {
  const { t } = useTranslation();
  const {
    usersCurrentLocation,
    markersLoading,
    pageCountryCode,
    listOfPartnersNames,
    resetForm,
    submitForm,
    certifiedCompetencesOptions,
    productCompetencesOptions,
    isCheckout,
  } = props;

  const { register, watch, setValue, control, getValues } = useFormContext<SASearchFormFields>();

  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [addressSuggestions, setAddressSuggestions] = useState<Suggestion[]>([]);
  const [partnerSuggestions, setPartnerSuggestions] = useState<Suggestion[]>([]);
  const [selectedOption, setSelectedOption] = useState<Suggestion | null>(null);
  const [isFilterChanged, setIsFilterChanged] = useState<boolean>(false);

  const groupedSelectRef = useRef<GroupedSelectRef>(null);
  const spFinderFiltersRef = useRef<SPFinderFiltersRef>(null);
  const isInitialized = useRef(false);

  const preferredSalesPartner = usePreferredSalesPartner();

  const filterBySalesPartner = watch("filterBySalesPartner");
  const filterByCountry = watch("filterByCountry");
  const selectedProductKeys = watch("productCompetence") ?? [];
  const selectedServiceKeys = watch("certifiedCompetence") ?? [];
  const radius = watch("radius");

  const screenSize = useNumericScreenSize();
  const isTablet = screenSize <= useNumericScreenSize.tablet;
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const { fetchData: getAutocomplete } = useGoogleAutocomplete();

  useEffect(() => {
    register("filterBySalesPartner");
    register("productCompetence");
    register("certifiedCompetence");
    register("address");
    register("partner");
    register("searchType");
  }, [register]);

  useEffect(() => {
    if (!markersLoading) {
      isInitialized.current = true;
    }
  }, [markersLoading]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (isInitialized.current) {
        setIsFilterChanged(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch]);

  useEffect(() => {
    // on each filter value change the submitForm is called except in mobile view.
    isFilterChanged && !isMobile && submitForm();
  }, [isFilterChanged, radius, selectedProductKeys, selectedServiceKeys, filterByCountry, filterBySalesPartner]);

  useEffect(() => {
    let address = null;
    if (usersCurrentLocation !== null) {
      address = usersCurrentLocation.fullAddress;
    } else if (pageCountryCode) {
      address = t(`country.${pageCountryCode}`);
    }
    if (address) {
      setSelectedOption({ label: address, group: SearchTypesSP.Address });
      onSearchInputChange(address);
      setValue("address", address);
    }
  }, [usersCurrentLocation, pageCountryCode]);

  useEffect(() => {
    if (selectedOption == null) {
      setValue("partner", "");
      setValue("address", "");
      setValue("searchType", SearchTypesSP.Address);
    } else if (selectedOption !== null && typeof selectedOption !== "string") {
      const name = selectedOption.group;
      const value = selectedOption.label;
      if (name == "address") {
        setValue("address", value);
        setValue("partner", "");
        setValue("searchType", SearchTypesSP.Address);
      } else if (name == "partner") {
        setValue("partner", value);
        setValue("address", "");
        setValue("searchType", SearchTypesSP.Partner);
      }
    }
    isFilterChanged && !isMobile && submitForm();
  }, [selectedOption, isFilterChanged]);

  const handleProductChange = (event: SelectChangeEvent<typeof selectedProductKeys>) => {
    const {
      target: { value },
    } = event;
    setValue("productCompetence", typeof value === "string" ? value.split(",") : value);
    trackPartnerFinderSearch(getValues(), "Filter Applied", "Click", undefined, "Products");
  };

  const handleServiceChange = (event: SelectChangeEvent<typeof selectedServiceKeys>) => {
    const {
      target: { value },
    } = event;
    setValue("certifiedCompetence", typeof value === "string" ? value.split(",") : value);
    trackPartnerFinderSearch(getValues(), "Filter Applied", "Click", undefined, "Services");
  };

  const getLabelByKey = (key: string, options: Option[]) => {
    return options.find((item) => item.key === key)?.label || key;
  };

  const onSearchInputChange = useCallback(
    throttle(async (newValue: string) => {
      if (newValue.length > 1) {
        const [predictions, res] = await Promise.all([
          getAutocomplete(newValue.trimLeft()),
          getCompanyList(newValue.trim()),
        ]);

        const addressSuggestions = predictions.map((p) => ({
          label: p.description,
          group: "address",
        }));

        const partnerSuggestions = res.salesPartnerNames.map((name: string) => ({
          label: name,
          group: "partner",
        }));

        setAddressSuggestions(addressSuggestions);
        setPartnerSuggestions(partnerSuggestions);
      }
    }, 1000),
    [listOfPartnersNames],
  );

  const handlePreferredSalesPartnerChange = (newVal: boolean, onChange: (value: boolean) => void) => {
    const newValue = !filterBySalesPartner;
    if (newValue) {
      trackSalesPartnerFinderSelectPreferred();
    }
    onChange(newVal);
    trackPartnerFinderSearch(getValues(), "Filter Applied", "Click", undefined, "My Preffered sales partner");
  };

  const handleCountryCheckbox = (newVal: boolean, onChange: (value: boolean) => void) => {
    if (!newVal) {
      trackSalesPartnerFinderDeselectCountry();
    } else {
      trackSalesPartnerFinderSelectCountry();
    }
    onChange(newVal);
    trackPartnerFinderSearch(getValues(), "Filter Applied", "Click", undefined, "My Country");
  };

  const sortedSuggestions = (data: Suggestion[]) =>
    data.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  const suggestedCombinedList = [...sortedSuggestions(addressSuggestions), ...sortedSuggestions(partnerSuggestions)];

  const footer = (
    <div className={classes.footerContainer}>
      {isCheckout && !isFilterChanged && !isMobile && (
        <Alert severity={"info"} className={classes.filterInfo}>
          {t("partnerFinder.filter.alert")}
        </Alert>
      )}

      <div className={classes.actionContainer}>
        <Button
          data-test-id={"find-partner"}
          variant="text"
          disabled={markersLoading}
          onClick={() => {
            resetForm();
            setSelectedOption(null);
          }}
          fullWidth={isMobile}
          startIcon={!isMobile && <Icon type={IconType.Reload} className={classes.resetIcon} />}
        >
          {t("searchPage.refinement.button.clearAll")}
        </Button>
        {isMobile && (
          <Button
            type={"submit"}
            data-test-id={"find-partner"}
            onClick={() => {
              submitForm();
              setShowFilterDialog(false);
            }}
            fullWidth={isMobile}
            startIcon={!isMobile && <SearchIcon />}
          >
            {isMobile ? t("searchPage.refinement.button.apply") : t("partnerFinder.search.findButton")}
          </Button>
        )}
      </div>
    </div>
  );

  const filtersCheckbox = (
    <>
      <Grid size={isTablet ? 12 : 6}>
        <div className={classes.filterCountry}>
          <div className="f-label-color">{t("salesPartner.searchCard.checkboxLabel.showOnly")}</div>
          <div className={classes.filterCheckbox}>
            <Controller
              name={"filterByCountry"}
              render={({ field: { value, onChange } }) => (
                <CheckboxWithLabel
                  checked={value}
                  data-test-id={"country-checkbox"}
                  onCheck={(newVal: boolean) => {
                    handleCountryCheckbox(newVal, onChange);
                  }}
                >
                  {usersCurrentLocation
                    ? `${t("salesPartner.searchCard.myCountry")} (${usersCurrentLocation.country.name})`
                    : `${t("salesPartner.searchCard.myCountry")} (${t(`country.${pageCountryCode}`)})`}
                </CheckboxWithLabel>
              )}
              control={control}
            />
            <Controller
              name={"filterBySalesPartner"}
              render={({ field: { value, onChange } }) => (
                <CheckboxWithLabel
                  checked={value}
                  disabled={!preferredSalesPartner}
                  onCheck={(newVal: boolean) => {
                    handlePreferredSalesPartnerChange(newVal, onChange);
                  }}
                >
                  {preferredSalesPartner
                    ? t("affiliateSalesPartner.finder.form.checkbox.preferredSalesPartner", {
                        name: preferredSalesPartner?.name,
                      })
                    : t("affiliateSalesPartner.finder.form.checkbox.disabledPreferredSalesPartner")}
                </CheckboxWithLabel>
              )}
              control={control}
            />
          </div>
        </div>
      </Grid>
      <Grid size={12}>
        <SPFinderFilters
          ref={spFinderFiltersRef}
          certifiedCompetencesOptions={certifiedCompetencesOptions}
          productCompetencesOptions={productCompetencesOptions}
          handleProductChange={handleProductChange}
          handleServiceChange={handleServiceChange}
          getLabelByKey={getLabelByKey}
          selectedProductKeys={selectedProductKeys}
          selectedServiceKeys={selectedServiceKeys}
        />
      </Grid>
      {isCheckout && !isFilterChanged && isMobile && (
        <Alert severity={"info"} className={classes.filterInfo}>
          {t("partnerFinder.filter.alert")}
        </Alert>
      )}
    </>
  );

  const autoCompleteSearch = (showFilterButton: boolean) => (
    <GroupedSelect
      ref={groupedSelectRef}
      disabled={markersLoading}
      openFilterDialog={() => setShowFilterDialog(true)}
      showFilterButton={showFilterButton}
      selectedOption={selectedOption}
      suggestedCombinedList={suggestedCombinedList}
      onSearchInputChange={onSearchInputChange}
      setSelectedOption={setSelectedOption}
    />
  );

  return (
    <>
      {showFilterDialog && isMobile && (
        <Modal
          title="Filter"
          onClose={() => setShowFilterDialog(false)}
          footerChildren={footer}
          footerClassName={classes.filterFooter}
        >
          {autoCompleteSearch(false)}
          <RadiusSelectButtonGroup />
          {filtersCheckbox}
        </Modal>
      )}
      <Box className={classes.searchBox}>
        <Grid container spacing={2}>
          <Grid size={isTablet ? 12 : 6}>{autoCompleteSearch(true)}</Grid>
          {!isMobile && filtersCheckbox}
          {!isMobile && <Grid size={12}>{footer}</Grid>}
        </Grid>
      </Box>
    </>
  );
};

export default memo(SalesPartnerFinderSearchBox);
